import { call, select } from "redux-saga/effects";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import CustomerFaceTimeCardData from "@app/scene/dashboard/store/cards/customerFaceTime/CustomerFaceTimeData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import CustomerFaceTimeCardConfiguration, {
  CustomerFaceTimeAggregationType,
} from "@app/types/dashboard/cards/customerFaceTime/CustomerFaceTimeCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { granularityByDateRange } from "./common";

interface CustomerFaceTimeCountAggregationItem {
  doc_count: number;
  key: number; // date in numeric format
  secondary: {
    doc_count: number;
    key: number;
    sum_faceTime: {
      value: number;
    };
  }[]; // key === 1 for verified and === 0 for unverified
}

export function* fetchCustomerFaceTimeCardDataHelper(params: {
  callback: (data: CustomerFaceTimeCardData) => void;
  configuration: CustomerFaceTimeCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filters = {
    assigneeId: assigneeIds ? { [PlatformFilterOperator.CONTAINS]: assigneeIds } : undefined,
    checkinTime: { $ne: null },
    checkoutTime: { $ne: null },
    completedAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    crmActivityTypeId: criteria.activityTypeIds.length
      ? { $in: criteria.activityTypeIds }
      : undefined,
    reliability: { $ne: null },
  };

  const payload = {
    $aggs: {
      primaryGroup: {
        field: "completedAt",
        interval: granularity,
        offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
        timezone: getLocalTimeZone(),
      },
      secondaryGroup: {
        field: "reliability",
      },
      sum: ["faceTime"],
    },
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<Activity, CustomerFaceTimeCountAggregationItem[]> =
    yield callApi("fetchActivities", orgId, payload);

  const history: CustomerFaceTimeCardData["history"] = response.aggregations.map((item) => {
    const verifiedItem = item.secondary.find(({ key }) => key === 1);
    const unverifiedItem = item.secondary.find(({ key }) => key === 0);
    return {
      date: parseApiDate(item.key),
      unverifiedTime: criteria.includeUnverified
        ? (unverifiedItem?.sum_faceTime.value ?? 0) /
          (criteria.aggregationType === CustomerFaceTimeAggregationType.SUM
            ? 1
            : (verifiedItem?.doc_count ?? 1))
        : undefined,
      verifiedTime:
        (verifiedItem?.sum_faceTime.value ?? 0) /
        (criteria.aggregationType === CustomerFaceTimeAggregationType.SUM
          ? 1
          : (verifiedItem?.doc_count ?? 1)),
    };
  });

  const result: CustomerFaceTimeCardData = {
    history: preferences.showChart ? history : undefined,
    verifiedTime: 0,
  };

  result.verifiedTime = history?.reduce((sum, item) => sum + item.verifiedTime, 0) ?? 0;
  if (criteria.includeUnverified) {
    result.unverifiedTime = history?.reduce((sum, item) => sum + (item.unverifiedTime ?? 0), 0);
  }

  if (result.history && preferences.cumulative) {
    let verifiedSum = 0;
    let unverifiedSum = 0;
    result.history = result.history.map((item) => {
      verifiedSum += item.verifiedTime;
      unverifiedSum += item.unverifiedTime ?? 0;
      return { ...item, unverifiedTime: unverifiedSum, verifiedTime: verifiedSum };
    });
  }

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const payload = {
      $filters: {
        ...filters,
        ...(criteria.includeUnverified ? {} : { reliability: { $eq: true } }),
        completedAt: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
      $limit: 0,
    };
    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, payload);
    result.previousCount = response.total;
  }

  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId)) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        $and: [
          ...(criteria.activityTypeIds.length
            ? [{ crmActivityTypeId: { $in: criteria.activityTypeIds } }]
            : []),
        ],
        date: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
        includePerformanceMetric: true,
        property: { $in: [LeaderboardMetricFieldName.TOTAL_FACE_TIME] },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const rank = isTeamScope
      ? response.data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : response.data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
